import React, {useState} from "react";
import {Button, Menu, MenuItem} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {jsPDF} from "jspdf";
import PropTypes from "prop-types";
import sanitize from "sanitize-filename";
import autoTable from "jspdf-autotable";

const ExportButton = ({dataGrid, columns, rows, tableRef, filename = null}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportToCSV = () => {
    const options = filename ? {fileName: sanitiseFilename(filename)} : {};
    dataGrid.current.exportDataAsCsv(options); // Trigger CSV export
  };

  const basicTablePdf = () => {
    const doc = new jsPDF({orientation: 'landscape'})

    const headingArray = [columns.map(column => column.headerName)]
    const rowsArray = rows.map(item =>
      Object.keys(item)
        .filter(key => key.startsWith("col"))
        .sort()
        .map(key => {
          let html = item[key];
          let div = document.createElement("div");
          div.innerHTML = html;

          return div.textContent || div.innerText || "";
        })
    );

    autoTable(doc, {
      head: headingArray,
      body: rowsArray,
    })

    doc.save(filename ? filename + '.pdf' : 'table-export.pdf')
  };

  const exportHtmlPdf = () => {
    const doc = new jsPDF({orientation: 'landscape'});
    const originalTable = tableRef.current;
    const maxColumns = 5;

    const headerCells = originalTable.querySelectorAll('thead tr th');
    const totalColumns = headerCells.length;

    for (let i = 0; i < totalColumns; i += maxColumns) {
      const tableClone = originalTable.cloneNode(true);

      Array.from(tableClone.rows).forEach(row => {
        Array.from(row.cells).forEach((cell, index) => {
          if (index < i || index >= i + maxColumns) {
            cell.remove();
          }
        });
      });

      if (i > 0) doc.addPage();

      autoTable(doc, {
        html: tableClone,
        willDrawCell: (data) => {
          const raw = data.cell.raw;
          if (!raw) return;

          const html = raw.innerHTML;
          if (!/<(b|strong|i|em)>/i.test(html)) return;

          data.cell.text = ''; // Clear default text rendering
        },
        didDrawCell: (data) => {
          const raw = data.cell.raw;
          if (!raw) return;

          const html = raw.innerHTML;
          if (!/<(b|strong|i|em|a)(\s+[^>]*)?>/i.test(html)) return;

          const x = data.cell.x + data.cell.padding('left');
          const y = data.cell.y + data.cell.padding('top') // Adjust for baseline
          const cellRightEdge = data.cell.x + data.cell.width - data.cell.padding('right');

          const parser = new DOMParser();
          const fragment = parser.parseFromString(`<div>${html}</div>`, 'text/html').body;

          let currentX = x;
          let currentY = y; // baseline offset
          const fontSize = data.cell.styles.fontSize || 10;

          function renderNode(node, fontStyle = 'normal') {
            if (node.nodeType === Node.TEXT_NODE) {
              const text = node.nodeValue;
              if (!text || !text.trim()) return;
              renderWords(text, fontStyle);
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              const tag = node.tagName.toLowerCase();
              let childStyle = fontStyle;
              if (tag === 'b' || tag === 'strong') {
                childStyle = fontStyle.includes('italic') ? 'bolditalic' : 'bold';
              } else if (tag === 'i' || tag === 'em') {
                childStyle = fontStyle.includes('bold') ? 'bolditalic' : 'italic';
              } else if (tag === 'a') {
                const href = node.getAttribute('href');
                const text = node.textContent;
                if (href && text) {
                  renderWords(text, 'normal', { link: href });
                  return;
                }
              }
              node.childNodes.forEach(child => renderNode(child, childStyle));
            }
          }

          function renderWords(text, style, options = {}) {
            doc.setFont(undefined, style);
            doc.setFontSize(fontSize);
            const words = text.split(/(\s+)/);
            for (let word of words) {
              const wordWidth = doc.getTextWidth(word);
              if (currentX + wordWidth > cellRightEdge) {
                currentX = x;
                currentY += fontSize * 0.4;
              }
              doc.text(word, currentX, currentY, { baseline: 'top' });
              if (options.link) {
                const underlineY = currentY + fontSize * 0.4;
                doc.setDrawColor(0, 0, 0);
                doc.line(currentX, underlineY, currentX + wordWidth, underlineY);
                doc.setDrawColor(0);
                doc.link(currentX, currentY, wordWidth, fontSize * 1.1, { url: options.link });
              }
              currentX += wordWidth;
            }
          }

          Array.from(fragment.childNodes).forEach(child => renderNode(child));
        },
        didParseCell: (data) => {
          const raw = data.cell.raw;
          if (!raw) return;

          const html = raw.innerHTML;

          if (/<p>/i.test(html)) {
            data.cell.styles.cellPadding = {
              top: 2,
              right: data.cell.styles.cellPadding.right,
              bottom: 5, // Adjust this value as needed
              left: data.cell.styles.cellPadding.left,
            };
          }
        }
      });
    }

    doc.save(filename ? filename + '.pdf' : 'table-export.pdf');
  }

  const generatePdf = () => {
    if (drupalSettings.feature_toggle.enabled.includes('table_field_markup_3612')) {
      return exportHtmlPdf();
    }

    return basicTablePdf();
  }

  const sanitiseFilename = (filename) => {
    return sanitize(filename.replace(/\s+/g, '_'));
  }


  return (<div>
    <Button
      className={'export-button'}
      startIcon={<FileDownloadIcon/>}
      endIcon={<ArrowDropDownIcon/>}
      onClick={handleClick}
    >
      Export
    </Button>
    <Menu sx={{fontSize: '14px'}} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}
          disableScrollLock>
      <MenuItem sx={{fontSize: '14px'}} onClick={() => handleExportToCSV()}>Export as CSV</MenuItem>
      <MenuItem sx={{fontSize: '14px'}} onClick={() => generatePdf()}>Export as PDF</MenuItem>
    </Menu>
  </div>);
};


ExportButton.propTypes = {
  dataGrid: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  tableRef: PropTypes.object.isRequired,
  filename: PropTypes.string,
};

export default ExportButton;
